import { PulseStorage, PulseStorageOptions, SetCookie, StorageChange } from '@pulse/common';
import { Subject, filter } from 'rxjs';
import { PulseEnvironment } from '../../../globals';
import { CookieChangeDetector } from './change-detector';
import { CookieParser } from './cookie-parser';

export class CookieStorage extends PulseStorage {
    freeSpace$: Subject<void> = new Subject();
    private cookies = new Map<string, string>();

    constructor(
        private env: PulseEnvironment,
        private parser: CookieParser,
        private changeDetector: CookieChangeDetector,
        options: PulseStorageOptions,
    ) {
        super(options);
        this.pull(false);

        this.changeDetector
            .changes()
            .pipe(filter((external) => external || !options.storagePrefix))
            .subscribe(() => this.pull());
    }

    get(key: string): string | null {
        return this.cookies.get(key) ?? null;
    }

    set(key: string, value: string): void {
        const cookie: SetCookie = {
            name: this.getStorageKey(key),
            value,
        };

        this.cookies.set(key, value);
        const cookieStr = this.parser.serializeSetCookie(cookie);
        this.env.doc.cookie = cookieStr;
        this.emitChange({ key, value, external: false });
    }

    keys(): string[] {
        return Array.from(this.cookies.keys());
    }

    remove(key: string): void {
        const cookie: SetCookie = {
            name: this.getStorageKey(key),
            value: '',
            expires: 0,
        };

        this.cookies.delete(key);
        const cookieStr = this.parser.serializeSetCookie(cookie);
        this.env.doc.cookie = cookieStr;
        this.emitChange({ key, value: null, external: false });
    }

    clear() {
        const keys = this.keys();

        for (const key of keys) {
            this.remove(key);
        }
    }

    all(): Record<string, string> {
        return Array.from(this.cookies).reduce<Record<string, string>>((jar, [key, value]) => {
            if (typeof value === 'string') {
                jar[key] = value;
            }

            return jar;
        }, {});
    }

    private pull(emitChanges = true) {
        const currentCookies = this.cookies;
        const newCookies = this.fetchCookies();
        const keys = new Set([...currentCookies.keys(), ...newCookies.keys()]);
        this.cookies = newCookies;

        if (!emitChanges) {
            return;
        }

        for (const key of keys) {
            const oldValue = currentCookies.get(key);
            const newValue = newCookies.get(key) ?? null;

            if (oldValue !== newValue) {
                const change: StorageChange = {
                    key,
                    value: newValue,
                    external: true,
                };

                this.emitChange(change);
            }
        }
    }

    private fetchCookies() {
        const doc = this.env.doc;
        const cookies = this.parser
            .parseCookies(doc.cookie)
            .filter((c) => this.isStorageKey(c.name));

        for (const cookie of cookies) {
            cookie.name = this.revertStorageKey(cookie.name);
        }

        const mapData: Array<[string, string]> = cookies.map((c) => [c.name, c.value]);

        return new Map(mapData);
    }
}
